import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import ImageUpload from '../common/ImageUpload';
// IMPORTANTE: Verifica que esta ruta apunte correctamente a tu custom hook
import { useImageUpload } from '../../hooks/useImageUpload';
import { useCategorias } from '../../hooks/useCategorias';

const initialForm = {
  nombre: '',
  descripcion: '',
  precio: '',
  CodCategoria: 1,
  imagen: '',
  orden: 1,
  activo: true,
};

const ProductoModal = ({ show, onHide, onSave, productoEdit }) => {
  const [formData, setFormData] = useState(initialForm);
  const [selectedFile, setSelectedFile] = useState(null); // Nuevo estado para el archivo en memoria
  const [saving, setSaving] = useState(false);

  // Instanciamos el hook con tu bucket
  const { uploadImage, deleteImage, extractPathFromUrl } = useImageUpload('productos-lumiere');
  
  // Hook para cargar categorías
  const { categorias, loading: loadingCategorias } = useCategorias();

  useEffect(() => {
    if (productoEdit) {
      // Si el producto viene con la relación categorias, extraer el ID
      const formDataToSet = {
        ...productoEdit,
        CodCategoria: productoEdit.CodCategoria || productoEdit.categorias?.id || 1
      };
      setFormData(formDataToSet);
    } else {
      setFormData(initialForm);
    }
    // Limpiamos el archivo seleccionado cada vez que se abre/cierra el modal
    setSelectedFile(null);
  }, [productoEdit, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      let finalImageUrl = formData.imagen;
      let oldImageUrl = productoEdit?.imagen || null;

      // Si el usuario seleccionó una imagen nueva, la subimos al bucket AHORA
      if (selectedFile) {
        const { url } = await uploadImage(selectedFile);
        finalImageUrl = url;
      }

      // Limpiar datos antes de guardar (eliminar campos de relaciones y campos obsoletos)
      const { categorias, categoria, id, created_at, updated_at, ...cleanData } = formData;
      
      // Preparar datos limpios para guardar
      const dataToSave = {
        nombre: cleanData.nombre,
        descripcion: cleanData.descripcion,
        precio: parseFloat(formData.precio),
        CodCategoria: parseInt(formData.CodCategoria, 10),
        imagen: finalImageUrl,
        orden: parseInt(formData.orden, 10),
        activo: formData.activo,
      };
      
      // Si estamos editando, incluir el ID
      if (productoEdit?.id) {
        dataToSave.id = productoEdit.id;
      }
      
      // Mandamos a guardar el producto con la URL final
      await onSave(dataToSave);

      // Borrar imagen anterior de storage si fue reemplazada o eliminada
      // Lo hacemos DESPUÉS de guardar en DB exitosamente
      if (oldImageUrl && oldImageUrl !== finalImageUrl) {
        const pathToDelete = extractPathFromUrl(oldImageUrl);
        if (pathToDelete) {
          try {
            await deleteImage(pathToDelete);
          } catch (delErr) {
            console.error('Error deleting old image from storage:', delErr);
          }
        }
      }

      onHide();
    } catch (err) {
      console.error(err);
      alert('Error al guardar el producto o subir la imagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="producto-modal">
      <Modal.Header closeButton>
        <Modal.Title>{productoEdit ? 'Editar Producto' : 'Nuevo Producto'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={7}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Precio (₡)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="precio"
                      value={formData.precio}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoría</Form.Label>
                    <Form.Select
                      name="CodCategoria"
                      value={formData.CodCategoria}
                      onChange={handleChange}
                      disabled={loadingCategorias}
                    >
                      {loadingCategorias ? (
                        <option>Cargando categorías...</option>
                      ) : (
                        categorias.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nomCategoria}
                          </option>
                        ))
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Orden de visualización</Form.Label>
                    <Form.Control
                      type="number"
                      name="orden"
                      value={formData.orden}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id="activo-switch"
                    label="Producto Activo"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            </Col>
        <Col md={5}>
          <div className="imagen-upload-panel">
            <ImageUpload
              currentImage={formData.imagen}
              onFileSelect={(file) => setSelectedFile(file)}
              onRemoveImage={() => {
                setSelectedFile(null);
                setFormData((prev) => ({ ...prev, imagen: '' }));
              }}
            />
          </div>
        </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" style={{ backgroundColor: '#c4956a', borderColor: '#c4956a' }} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Producto'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ProductoModal;